// ServicesShowcase.jsx
import React, { useRef, useEffect, useState } from "react";

export default function ServicesShowcase() {
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);      // for carousel + glow
  const [overlayIndex, setOverlayIndex] = useState(null); // for mobile gold plate

  const services = [
    {
      emoji: "🧩",
      title: "Custom Business Software",
      text: "Tailor-made software built around your workflow — not the other way around.",
      benefit: "Cut manual work, reduce errors, and keep your entire operation in sync.",
    },
    {
      emoji: "⚙️",
      title: "Workflow Automation",
      text: "Internal tools, dashboards, and data-driven workflow automation.",
      benefit: "Save hours every week by automating repetitive tasks and approvals.",
    },
    {
      emoji: "🖥️",
      title: "Custom Websites",
      text: "Beautiful, mobile-friendly websites built for speed, SEO, and conversions.",
      benefit: "Turn visitors into inquiries, bookings, and paying customers 24/7.",
    },
    {
      emoji: "🛒",
      title: "Shopify Stores",
      text: "Sell online with checkout, product listings, payments, and analytics.",
      benefit: "Launch a store that’s fast, trustworthy, and easy for customers to buy from.",
    },
    {
      emoji: "📱",
      title: "Mobile & Web Apps",
      text: "React, React Native, and modern web apps tailored to your business.",
      benefit: "Give your customers and team tools they can use anywhere, on any device.",
    },
    {
      emoji: "📊",
      title: "Data & Analytics",
      text: "Dashboards, KPI tracking, reports, and business intelligence tools.",
      benefit: "See what’s working, what’s not, and make decisions backed by real data.",
    },
  ];

  // ---------- MOBILE: detect active center card on scroll ----------
  const handleScroll = () => {
    const container = carouselRef.current;
    if (!container || window.innerWidth >= 640) return;

    const cardItems = [...container.querySelectorAll("[data-service-card]")];
    const center = container.scrollLeft + container.offsetWidth / 2;

    let newIndex = 0;
    let closest = Infinity;

    cardItems.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(center - cardCenter);
      if (distance < closest) {
        closest = distance;
        newIndex = index;
      }
    });

    setActiveIndex(newIndex);
  };

  // ---------- MOBILE: scale + glow active card ----------
  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 640) return;

    const items = document.querySelectorAll("[data-service-card]");
    items.forEach((card, i) => {
      const active = i === activeIndex;
      card.style.transform = active ? "scale(1)" : "scale(0.92)";

      const inner = card.querySelector(".service-inner");
      if (active) inner.classList.add("active-service-card");
      else inner.classList.remove("active-service-card");
    });
  }, [activeIndex]);

  // ---------- Fade-in on scroll (desktop + mobile) ----------
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const cards = section.querySelectorAll(".service-fade");
    const heading = section.querySelector(".service-heading-fade");
    const subheading = section.querySelector(".service-subheading-fade");

    // Hero section timing:
    // - Intro animation: ~2600ms
    // - Hero delay: 2500ms  
    // - Hero items animation: 4 items × 200ms = 800ms
    // - Hero visuals animation: starts at 600ms, takes ~1000ms
    // Total: ~4100ms from page load
    // Add buffer for smooth transition: 4500ms minimum delay
    const minimumDelay = 4500;
    
    // Get page load time (more accurate than component mount time)
    const getPageLoadTime = () => {
      if (window.performance && window.performance.timing) {
        return window.performance.timing.navigationStart || Date.now();
      }
      return Date.now();
    };
    
    const pageLoadTime = getPageLoadTime();
    let hasTriggered = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered) {
          hasTriggered = true;
          const elapsed = Date.now() - pageLoadTime;
          const remainingDelay = Math.max(0, minimumDelay - elapsed);
          
          // Wait for Hero to finish, then animate Services section
          setTimeout(() => {
            // Animate heading first
            if (heading) {
              setTimeout(() => heading.classList.add("visible"), 0);
            }
            
            // Animate subheading after heading
            if (subheading) {
              setTimeout(() => subheading.classList.add("visible"), 150);
            }
            
            // Animate cards after heading/subheading
            cards.forEach((card, i) => {
              setTimeout(() => card.classList.add("visible"), 300 + (i * 120));
            });
            
            observer.disconnect();
          }, remainingDelay);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(section);
  }, []);

  // ---------- Handle mobile tap to toggle gold overlay ----------
  const handleCardClick = (index) => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 640) return; // desktop uses hover only

    setOverlayIndex((prev) => (prev === index ? null : index));
  };

  return (
    <>
      <style>
        {`
  .service-fade {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
  .service-fade.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .service-heading-fade {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .service-heading-fade.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .service-subheading-fade {
    opacity: 0;
    transform: translateY(15px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
  .service-subheading-fade.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .active-service-card {
    box-shadow: 0 0 25px rgba(120, 95, 20, 0.25);
    border-color: rgba(120, 95, 20, 0.4);
  }

  .service-inner {
    position: relative;
    overflow: hidden;
  }

  /* -------- FULL CARD SLIDE UP -------- */
  .gold-overlay {
    position: absolute;
    inset: 0;
    padding: 1.2rem 1.4rem;
    color: #1f1a12;
    font-size: 0.9rem;
    line-height: 1.45;
    display: flex;
    flex-direction: column;
    justify-content: center;

    /* Start hidden below card */
    transform: translateY(100%);
    transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);

position: relative;
overflow: hidden;
background: linear-gradient(
  135deg,
  #f8e7b3 0%,
  #d6b365 25%,
  #b8923a 50%,
  #e3c076 75%,
  #f4e3b0 100%
);
}

.card-shine::after {
  content: "";
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    120deg,
    transparent,
    rgba(255,255,255,0.7),
    transparent
  );
  transform: skewX(-20deg);
  animation: shine 2.5s ease-in-out infinite;
}

@keyframes shine {
  0% { left: -150%; }
  60% { left: 150%; }
  100% { left: 150%; }
}



    background-size: 200% 200%;
    animation: goldShine 4s ease-in-out infinite;
    box-shadow: inset 0 0 18px rgba(0,0,0,0.15);
  }

  @keyframes goldShine {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  /* Desktop hover reveal */
  @media (min-width: 640px) {
    .service-inner:hover .gold-overlay {
      transform: translateY(0%);
    }
  }

  /* Mobile reveal */
  .gold-overlay.show {
    transform: translateY(0%);
  }

  .gold-heading {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
    letter-spacing: 1px;
  }
`}
      </style>


      <section ref={sectionRef} className="py-16 px-4 relative">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="service-heading-fade text-center text-xl sm:text-2xl font-copperplate text-[#785f14]">
            <span className="font-semibold">What We Build</span>
          </h2>

          <p className="service-subheading-fade text-gray-600 mt-2 max-w-xl mx-auto text-sm sm:text-base">
            Modern digital solutions that actually move the needle for your business.
          </p>

          {/* Fade edges (mobile only) */}
          <div className="pointer-events-none absolute left-0 top-[150px] h-40 w-10 bg-gradient-to-r from-white/80 to-transparent sm:hidden"></div>
          <div className="pointer-events-none absolute right-0 top-[150px] h-40 w-10 bg-gradient-to-l from-white/80 to-transparent sm:hidden"></div>

          {/* Carousel / Grid */}
          <div
            ref={carouselRef}
            onScroll={handleScroll}
            className="
              mt-10
              flex overflow-x-auto gap-4 px-2 py-6
              snap-x snap-mandatory scrollbar-hide
              sm:grid sm:grid-cols-2 lg:grid-cols-3
              sm:gap-6 sm:px-0 sm:overflow-visible
            "
          >
            {services.map((service, i) => (
              <div
                key={i}
                data-service-card
                onClick={() => handleCardClick(i)}
                className="
                  service-fade
                  min-w-[75%] sm:min-w-0 snap-center
                  transition-transform duration-300
                  will-change-transform
                  cursor-pointer
                "
              >
                <div
                  className="
                    service-inner p-5 rounded-xl border bg-white shadow-sm
                    transition-all duration-300 group
                    hover:shadow-lg hover:-translate-y-1 sm:hover:scale-[1.02]
                  "
                >
                  <div className="flex justify-center mb-3 text-4xl group-hover:scale-110 transition">
                    {service.emoji}
                  </div>

                  <h3 className="font-semibold text-[#785f14]">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm mt-2">
                    {service.text}
                  </p>

                  {/* GOLD OVERLAY PLATE */}
                  <div
                    className={
                      "gold-overlay card-shine" +
                      (overlayIndex === i ? "show" : "")
                    }
                  >
                    <span className="gold-heading">How this helps</span>
                    <p>{service.benefit}</p>
                  </div>

                </div>
              </div>
            ))}
          </div>

          {/* Dots (mobile only) */}
          <div className="flex justify-center mt-4 gap-2 sm:hidden">
            {services.map((_, i) => (
              <div
                key={i}
                className={`
                  w-2.5 h-2.5 rounded-full transition
                  ${activeIndex === i ? "bg-[#785f14]" : "bg-gray-300"}
                `}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
